import i18n from "i18next";
import { jsPDF } from "jspdf";
import autoTable, { RowInput, Styles } from "jspdf-autotable";
import { MapKind } from "../../../@adapters/map-export/mapglExport";
import { territoryApi } from "../../../api/territory";
import { addressToString } from "../../../helpers/address";
import { dateToString, localizedLanguageName, stringToLocaleDate, stringToLongLocaleDate } from "../../../helpers/dateHelpers";
import { t } from "../../../helpers/locale";
import {
  boldFont,
  getHeightForText,
  getWidthForLine,
  halfInch,
  largestFontSize,
  longestLine,
  newPDF,
  newPDFWithHeader,
  normalFont,
  PDFFooterLeft,
  S13Header,
  truncateToWidth,
} from "../../../helpers/pdf";
import { buildS13Pages, S13Row } from "../../../helpers/s13";
import { NBSP } from "../../../helpers/util";
import { Cong } from "../../../types/cong";
import { PDFOrientation } from "../../../types/paper";
import { CongSettings } from "../../../types/scheduling/settings";
import {
  Territory,
  TerritoryAddress,
  TerritoryAddressLocationType,
  TerritoryRecord,
} from "../../../types/scheduling/territory";
import User from "../../../types/user";
import { territoryFilename, terrLabel, tileProvider } from "./common";
import { returnByDate } from "./return_calc";
import { HGBugsnagNotify } from "../../../helpers/bugsnag";

type ColumnStyles = { [key: string]: Partial<Styles> };
const margin = halfInch;
const bodyFontSize = 8;
const headerFontSize = 8;

const smLineThinkness = 0.005;
const mdLineThinkness = 0.01;
const lgLineThinkness = 0.05;

export async function territoryS13(props: {
  territories: Territory[];
  territoryRecords: TerritoryRecord[];
  userMap: Map<number, User>;
  cong: Cong;
  years: number[];
  summary: boolean;
}) {
  const { territories, territoryRecords, userMap, cong, years, summary } = props;
  const pages = buildS13Pages(territories, territoryRecords, userMap, years, summary);

  // S-13 is always A4
  const doc = await newPDF({
    title: t("schedules.territory.s13.title"),
    localeCode: cong.locale.code,
    paperSize: "a4",
  });

  pages.forEach((page, i) => {
    if (i != 0) doc.addPage();

    const initY = S13Header(
      doc,
      t("schedules.territory.s13.title"),
      t("general.service-year"),
      page.serviceYear,
      page.business ? t("schedules.territory.business") : "",
    );
    doc.setFontSize(8);
    doc.text(`*${t("schedules.territory.s13.footnote")}`, margin, 10.7 + 0.2, {
      maxWidth: doc.internal.pageSize.width - margin * 2,
    });
    doc.text(`S-13-${cong.locale.symbol}`, margin, 10.7 + 0.5);
    const pagesText = `${i + 1}/${pages.length}`;
    const pagesTextWidth = doc.getTextWidth(pagesText);
    doc.text(pagesText, doc.internal.pageSize.width - margin - pagesTextWidth, 10.7 + 0.5);

    const curY = initY + 0.05;
    territoryRecordSheets(doc, curY, page.rows);
  });

  doc.save(`${cong.name}_${t("schedules.territory.territories")}_${years.join("-")}.pdf`);
}

function territoryRecordSheets(doc: jsPDF, initialY: number, rows: S13Row[]): void {
  const tableWidth = doc.internal.pageSize.width - margin * 2;
  const firstColWidth = getWidthForLine(doc, headerFontSize, t("schedules.territory.s13.terr-no"));
  const oneFifteenthWidth = tableWidth / 5 / 3;
  const rowHeight = oneFifteenthWidth / 2 - mdLineThinkness - smLineThinkness;
  const secondColWidth = oneFifteenthWidth * 2;
  const colWidth = (tableWidth - firstColWidth - secondColWidth) / 8;

  const colStyles = () => {
    const styleObj: ColumnStyles = {};
    Array.from(Array(10).keys()).forEach((i) => {
      styleObj[i] = {
        cellWidth: i === 0 ? firstColWidth : i === 1 ? secondColWidth : colWidth,
        lineWidth: smLineThinkness,
      };
    });
    return styleObj;
  };

  const drawTable = (head: RowInput[], body: RowInput[]) => {
    autoTable(doc, {
      theme: "grid",
      tableLineColor: "black",
      tableLineWidth: lgLineThinkness,
      startY: initialY,
      head,
      body,
      margin: {
        left: margin,
        bottom: 0,
        right: margin,
      },
      pageBreak: "avoid",
      rowPageBreak: "avoid",
      styles: {
        fontSize: bodyFontSize,
        font: normalFont,
        fontStyle: "normal",
        cellPadding: { top: 0.04, bottom: 0.04, left: 0.05, right: 0.05 },
        halign: "center",
        valign: "middle",
        minCellHeight: rowHeight,
        lineColor: "black",
      },
      headStyles: {
        font: boldFont,
        fontStyle: "bold",
        fillColor: [209, 209, 209],
        textColor: "black",
        lineColor: "black",
        lineWidth: smLineThinkness,
        fontSize: headerFontSize,
        cellPadding: 0.05,
        halign: "center",
        valign: "middle",
        minCellHeight: rowHeight,
      },
      columnStyles: colStyles(),
    });
  };

  const colWidths = [firstColWidth, secondColWidth, colWidth];
  const headers: RowInput[] = row(true, undefined, colWidths, doc);
  const theBody: RowInput[] = rows.flatMap((r) => row(false, r, colWidths, doc));

  drawTable(headers, theBody);
}

const row = (header: boolean, r: S13Row | undefined, colWidths: number[], doc: jsPDF): RowInput[] => {
  const territoryNumber = t("schedules.territory.s13.terr-no");
  const dateLastCompleted = `${t("schedules.territory.s13.last-date-completed")}*`;
  const assignedTo = t("schedules.territory.s13.assigned-to");
  const dateAssigned = t("schedules.territory.s13.date-assigned");
  const dateCompleted = t("schedules.territory.s13.date-completed");

  // we need to compute the font size for the headers so that they will fit and not have line breaks in wrong places
  type fontSizes = {
    terrNo: number;
    lastCompleted: number;
    assignedTo: number;
    dateAssigned: number;
    dateCompleted: number;
    assignee: (name?: string) => number;
  };
  const minFontSize = 6;
  const fontSize: fontSizes = {
    terrNo: header
      ? // @ts-ignore noUncheckedIndexedAccess
        largestFontSize(doc, headerFontSize, minFontSize, longestLine(doc, territoryNumber), colWidths[0])
      : bodyFontSize,
    // @ts-ignore noUncheckedIndexedAccess
    lastCompleted: largestFontSize(doc, headerFontSize, minFontSize, longestLine(doc, dateLastCompleted), colWidths[1]),
    // @ts-ignore noUncheckedIndexedAccess
    assignedTo: largestFontSize(doc, headerFontSize, minFontSize, longestLine(doc, assignedTo), colWidths[2]),
    // @ts-ignore noUncheckedIndexedAccess
    dateAssigned: largestFontSize(doc, headerFontSize, minFontSize, longestLine(doc, dateAssigned), colWidths[2] / 2),
    // @ts-ignore noUncheckedIndexedAccess
    dateCompleted: largestFontSize(doc, headerFontSize, minFontSize, longestLine(doc, dateCompleted), colWidths[2] / 2),
    assignee: (name?: string): number => {
      if (!name) return minFontSize;
      // @ts-ignore noUncheckedIndexedAccess
      return largestFontSize(doc, headerFontSize, minFontSize, name, colWidths[2]);
    },
  };

  const terrNumberAndLocality = (): string => {
    const maxWidth = (colWidths[0] ?? 0.25) - 0.12;
    const terrNumber = truncateToWidth(doc, r?.terrNumber ?? "", maxWidth ?? 0);
    const terrLocality = truncateToWidth(doc, r?.terrLocality ?? "", maxWidth ?? 0);
    return `${terrNumber}\n${terrLocality}`;
  };

  const terrNumberText = header ? territoryNumber : terrNumberAndLocality();
  const cell = (i: number) => r?.cells[i] ?? { assignedTo: "", dateAssigned: "", dateCompleted: "" };

  return [
    [
      {
        content: terrNumberText,
        rowSpan: 2,
        styles: {
          lineWidth: { left: lgLineThinkness, right: smLineThinkness, bottom: mdLineThinkness, top: mdLineThinkness },
          fontSize: fontSize.terrNo,
        },
      },
      {
        content: header ? dateLastCompleted : r?.lastWorked || "",
        rowSpan: 2,
        styles: {
          lineWidth: { right: lgLineThinkness, left: smLineThinkness, bottom: mdLineThinkness, top: mdLineThinkness },
          fontSize: fontSize.lastCompleted,
        },
      },
      ...[0, 1, 2, 3].map((i) => ({
        content: header ? assignedTo : cell(i).assignedTo || NBSP,
        colSpan: 2,
        styles: {
          lineWidth: {
            left: i === 0 ? lgLineThinkness : mdLineThinkness,
            right: mdLineThinkness,
            top: mdLineThinkness,
            bottom: smLineThinkness,
          },
          fontSize: header ? fontSize.assignedTo : fontSize.assignee(cell(i).assignedTo || undefined),
        },
      })),
    ],
    [0, 1, 2, 3].flatMap((i) => [
      {
        content: header ? dateAssigned : cell(i).dateAssigned || NBSP,
        styles: {
          lineWidth: { right: smLineThinkness, left: i === 0 ? lgLineThinkness : mdLineThinkness, bottom: mdLineThinkness },
          fontSize: fontSize.dateAssigned,
        },
      },
      {
        content: header ? dateCompleted : cell(i).dateCompleted || NBSP,
        styles: {
          lineWidth: { right: mdLineThinkness, left: smLineThinkness, bottom: mdLineThinkness },
          fontSize: fontSize.dateCompleted,
        },
      },
    ]),
  ];
};

async function getImageDimensions(src: Blob): Promise<{ width: number; height: number }> {
  const bmp = await createImageBitmap(src);
  const { width, height } = bmp;
  bmp.close();
  return { width: width, height: height };
}

// this creates a PDF of one territory, with its map and addresses, suitable for giving to a publisher
export async function singleTerritoryPDF(
  territory: Territory,
  addresses: TerritoryAddress[],
  cong: Cong,
  settings: CongSettings,
  imageBlob: Blob,
  orientation: PDFOrientation = "portrait",
  fullPage: boolean = false,
  kind: MapKind = MapKind.single,
  pageDimensions?: number[],
  records?: TerritoryRecord[],
) {
  const isPortrait = orientation === "portrait";
  const { doc, initY } =
    kind === MapKind.congregation
      ? await newPDFWithHeader(
          cong.name,
          i18n.t("schedules.territory.territories"),
          cong.locale.code,
          orientation,
          pageDimensions,
        )
      : await newPDFWithHeader(terrLabel(territory), cong.name, cong.locale.code, orientation);
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const docWidth = pageWidth - margin;
  doc.setFont(normalFont);
  doc.setFontSize(bodyFontSize);

  await addReturnByLabel(territory, settings, doc, records);

  let curY = initY;

  const addNotes = () => {
    if (!territory.notes) return;
    const txtLines: string[] = doc.splitTextToSize(territory.notes, docWidth - margin);
    doc.text(txtLines, margin, curY, { align: "left" });
    curY += getHeightForText(doc, bodyFontSize, txtLines.join("\n"), false);
  };

  addNotes();

  const addAttribution = () => {
    // add the attribution overlay, since the canvas in maplibre won't have it
    const origFontSize = doc.getFontSize();
    const origFillColor = doc.getFillColor();

    // add an element so that we can get the innerText of the HTML attribution
    const attribEl = document.createElement("div");
    attribEl.innerHTML = tileProvider(settings).attribution;
    const attributionText = attribEl.textContent || attribEl.innerText;
    attribEl.remove();

    doc.setFontSize(7);
    const textDim = doc.getTextDimensions(attributionText);
    const textHeight = textDim.h + 0.05;

    doc.saveGraphicsState();
    // @ts-ignore bad type def
    doc.setGState(new doc.GState({ opacity: 0.3 }));
    doc.setFillColor(255, 255, 255);
    doc.rect(margin, curY - textHeight, textDim.w + 0.1, textHeight, "F");
    doc.restoreGraphicsState();
    doc.text(attributionText, margin + 0.02, curY - textHeight / 2, { baseline: "middle", align: "left" });

    doc.setFillColor(origFillColor);
    doc.setFontSize(origFontSize);
  };

  let imageWidth = pageWidth - 1;
  // we want to maintain the aspect ratio
  const dimensions = await getImageDimensions(imageBlob);
  const aspectRatio = dimensions.width / dimensions.height;
  const calcImageHeight = (): number => {
    if (kind === MapKind.congregation) {
      return isPortrait ? imageWidth / aspectRatio : pageHeight - curY - 0.6;
    }
    if (isPortrait) return imageWidth / aspectRatio;
    return fullPage ? pageHeight - curY - 0.6 : pageHeight * 0.75 - curY - 0.1;
  };
  const imageHeight = calcImageHeight();
  if (!isPortrait) imageWidth = imageHeight * aspectRatio;

  doc.addImage(
    URL.createObjectURL(imageBlob),
    "PNG",
    (pageWidth - imageWidth) / 2,
    curY,
    imageWidth,
    imageHeight,
    "",
    "FAST",
  );
  curY += imageHeight;
  addAttribution();
  curY += 0.15;

  const tableBodyFontSize = bodyFontSize - 1;
  let notesWidth = pageWidth / 5;
  addresses.forEach((a) => {
    const width = getWidthForLine(doc, tableBodyFontSize, a.notes ?? "");
    if (width > notesWidth) notesWidth = width;
  });
  // don't let the notes column get bigger than 1/3 of the page width
  if (notesWidth > pageWidth / 3) notesWidth = pageWidth / 3;
  // we have two side by side address tables in landscape mode, so cut the notes width in half
  if (!isPortrait) notesWidth /= 2;

  const drawTable = (
    doc: jsPDF,
    startY: number,
    startX: number,
    tableWidth: number,
    header: RowInput[],
    body: RowInput[],
  ) => {
    const notesCol = settings.terr_address_hide_type ? 3 : 4;
    autoTable(doc, {
      theme: "striped",
      startY,
      tableWidth,
      head: header,
      headStyles: {
        fontSize: headerFontSize,
        font: boldFont,
        fontStyle: "bold",
        textColor: "white",
      },
      body: body,
      margin: {
        left: startX,
        right: margin,
      },
      pageBreak: "avoid",
      rowPageBreak: "avoid",
      styles: {
        fontSize: tableBodyFontSize,
        font: normalFont,
        textColor: "black",
        cellPadding: {
          top: 0.015,
          bottom: 0.015,
          left: 0.02,
          right: 0.02,
        },
      },
      columnStyles: {
        [notesCol]: {
          cellWidth: notesWidth,
        },
      },
      didDrawPage: (d) => {
        if (d.cursor) curY = d.cursor.y;
      },
    });
  };

  const hideType = settings.terr_address_hide_type;
  const hideNotes = !isPortrait && !addresses.filter((a) => a.notes !== null).length;

  const headerRow: string[] = [
    t("contact.address"),
    t("general.date"),
    ...(hideType ? [] : [t("general.type")]),
    t("congprofile.language"),
    ...(hideNotes ? [] : [t("userinfo.emergency.notes")]),
  ];

  const header: RowInput[] = [headerRow];

  const addressStatus = (ta: TerritoryAddress): string => {
    const items = [];
    if (ta.dnc) items.push(t("schedules.territory.do-not-call"));
    if (ta.locationType === TerritoryAddressLocationType.Business) items.push(t("schedules.territory.business"));
    else if (ta.locationType === TerritoryAddressLocationType.Custom) items.push(t("general.custom"));
    return items.join(", ");
  };

  const addrNotes = (a: TerritoryAddress): string | null => {
    let value = "";
    if (a.notes?.trim()) value = a.notes.trim();
    if (a.name?.trim()) value += "\n" + a.name.trim();
    if (a.phone?.trim()) value += " " + a.phone.trim();
    if (!value) return null;
    return value;
  };

  const body: RowInput[] = addresses.map((a) => {
    const pinLabel = a.pinLabel ? `${a.pinLabel} | ` : "";

    return [
      `${pinLabel}${addressToString(a)}`,
      a.lastWorked ? stringToLocaleDate(a.lastWorked) : null,
      ...(hideType ? [] : [addressStatus(a)]),
      a.lang ? (localizedLanguageName(i18n.language, a.lang) ?? null) : null,
      ...(hideNotes ? [] : [addrNotes(a)]),
    ];
  });

  if (body.length > 0 && kind === MapKind.single) {
    // only show the addresses header if we actually have addresses to display
    if (isPortrait) {
      drawTable(doc, curY, margin, pageWidth - margin * 2, header, body);
    } else {
      //split to two columns
      const tableWidth = (pageWidth - margin * 2) / 2 - 0.1;
      const startY = curY;
      const col1 = body.splice(0, Math.ceil(addresses.length / 2));
      const pageNum = doc.getNumberOfPages();
      drawTable(doc, startY, margin, tableWidth, header, col1);
      doc.setPage(pageNum);
      drawTable(doc, startY, margin + tableWidth + 0.1, tableWidth, header, body);
    }
  }

  doc.save(`${territoryFilename(territory)}.pdf`);
}

async function addReturnByLabel(territory: Territory, settings: CongSettings, doc: jsPDF, records?: TerritoryRecord[]) {
  if (!records) {
    try {
      records = await territoryApi.getRecords(true);
    } catch (err: any) {
      console.error("error getting territory records for return by label", err);
      HGBugsnagNotify("addReturnByLabel", err);
    }
  }
  const assignment = records?.find((r) => r.territory_id === territory.id && r.end === null);
  if (assignment) {
    const end = returnByDate(assignment.start, settings.terr_checkout_duration_days);
    const endStr = stringToLongLocaleDate(dateToString(end.toDate()));
    PDFFooterLeft(doc, `${t("schedules.territory.return-by", { date: endStr })}`);
  }
}
